import { gifAttachmentSchema, isGifMediaUrl, MESSAGE_MAX, type Conversation, type DirectMessage, type EncryptedBody, type GifAttachment } from "@atrium/shared";
import { api } from "../../lib/api";
import { decryptMessage, encryptMessage } from "../../lib/e2ee/crypto";
import { conversationKey, currentIdentity, distributeNewKey, publicKeys, settled, snapshot, trustedSigningKeys } from "../../lib/e2ee/session";
import { encryptedMedia, safeAttachments } from "../../lib/e2ee/files";
import { gifEmbed } from "../chat/ChatGif";

/*
 * Encrypted direct messages, between the wire format and what the chat UI
 * renders. Decrypted messages look like ordinary ones (content, GIF embed,
 * images) plus an `e2ee` state; nothing decrypted is ever sent back to the server.
 *
 * Encryption is on by default: a conversation turns it on before its next
 * message as soon as this device is unlocked and every member has keys.
 */

/** Decrypted text by message id, for reply previews (the server only knows the reply is encrypted). */
const plaintext = new Map<string, string>();

/** A decrypted GIF is only shown if it passes the same checks the server applies to plaintext GIFs. */
function safeGif(gif: unknown): GifAttachment | null {
  const parsed = gifAttachmentSchema.safeParse(gif);
  if (!parsed.success) return null;
  const g = parsed.data;
  return isGifMediaUrl(g.url) && isGifMediaUrl(g.previewUrl) && (!g.stillUrl || isGifMediaUrl(g.stillUrl)) ? g : null;
}

export async function decryptDm(m: DirectMessage): Promise<DirectMessage> {
  const withReply = (x: DirectMessage) =>
    x.replyTo?.encrypted ? { ...x, replyTo: { ...x.replyTo, content: plaintext.get(x.replyTo.id) ?? "Encrypted message" } } : x;
  if (!m.ciphertext) return withReply(m);
  if (m.e2ee === "ok") return withReply(m);
  if (snapshot().userId) await settled();
  // The raw media rows are ciphertext: never shown as they are.
  if (snapshot().status !== "ready") return withReply({ ...m, content: "", embeds: [], media: [], e2ee: "locked" });
  const p = m.ciphertext;
  try {
    const key = await conversationKey(m.conversationId, p.keyVersion);
    if (key) {
      for (const signer of [...(await trustedSigningKeys(m.author.id))].reverse()) {
        try {
          const body = await decryptMessage(p, key, { conversationId: m.conversationId, authorId: m.author.id, keyVersion: p.keyVersion }, signer);
          const content = body.content.slice(0, MESSAGE_MAX);
          const gif = body.gif ? safeGif(body.gif) : null;
          // Only files the server attached to this very message, so a descriptor can't point elsewhere.
          const served = new Map(m.media.map((x) => [x.id, x.url]));
          const media = safeAttachments(body.attachments)
            .filter((a) => served.has(a.mediaId))
            .map((a) => encryptedMedia(a, served.get(a.mediaId)!));
          plaintext.set(m.id, content.slice(0, 300));
          return withReply({ ...m, content, embeds: gif ? [gifEmbed(gif)] : [], media, e2ee: "ok" });
        } catch {
          /* try an older trusted key */
        }
      }
    }
  } catch {
    /* fall through */
  }
  return withReply({ ...m, content: "", embeds: [], media: [], e2ee: "failed" });
}

export const decryptDms = (list: DirectMessage[]) => Promise.all(list.map(decryptDm));

export async function decryptConversation(c: Conversation): Promise<Conversation> {
  return c.lastMessage?.ciphertext ? { ...c, lastMessage: await decryptDm(c.lastMessage) } : c;
}

export const decryptConversations = (list: Conversation[]) => Promise.all(list.map(decryptConversation));

/**
 * Whether messages here will be encrypted: they already are, or this device
 * is unlocked and every member has keys (so the next message turns it on).
 */
export async function willEncrypt(conv: Conversation, fresh = false): Promise<boolean> {
  if (conv.encryption.enabled) return true;
  if (snapshot().status !== "ready") return false;
  const keys = await publicKeys(conv.members.map((m) => m.id), fresh);
  return conv.members.every((m) => keys.has(m.id));
}

/** Turn encryption on before sending, when everyone can take part. Returns the conversation as it is now. */
export async function ensureEncrypted(conv: Conversation): Promise<Conversation> {
  if (conv.encryption.enabled || !(await willEncrypt(conv, true))) return conv;
  try {
    return await distributeNewKey(conv);
  } catch (e) {
    // Someone else turned it on first.
    if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 409) return api.get<Conversation>(`/api/conversations/${conv.id}`);
    throw e;
  }
}

/** Short text for previews (conversation list, reply bars) of an encrypted message that couldn't be read. */
export function encryptedPreview(m: Pick<DirectMessage, "e2ee">) {
  return m.e2ee === "locked" ? "Encrypted message (locked on this device)" : "Encrypted message";
}

/**
 * Encrypt a message body for a conversation, distributing a new key first if
 * the membership changed. Returns the payload and the conversation as it is now.
 */
export async function encryptForConversation(conv: Conversation, body: EncryptedBody): Promise<{ payload: NonNullable<DirectMessage["ciphertext"]>; conversation: Conversation }> {
  const me = currentIdentity();
  const myId = snapshot().userId;
  if (!me || !myId) throw new Error("Unlock encrypted messages on this device first");
  let c = conv;
  let key = c.encryption.rekeyNeeded ? null : await conversationKey(c.id, c.encryption.keyVersion);
  if (!key) {
    c = await distributeNewKey(c);
    key = await conversationKey(c.id, c.encryption.keyVersion);
    if (!key) throw new Error("Couldn't set up the conversation key");
  }
  const payload = await encryptMessage(body, key, { conversationId: c.id, authorId: myId, keyVersion: c.encryption.keyVersion }, me);
  return { payload, conversation: c };
}

/** Run an encrypted write; if the key moved under us (409), refresh the conversation and try once more. */
export async function withFreshKey<T>(conv: Conversation, run: (c: Conversation) => Promise<T>, onConversation?: (c: Conversation) => void): Promise<T> {
  try {
    return await run(conv);
  } catch (e) {
    if (!(e && typeof e === "object" && "status" in e && (e as { status: number }).status === 409)) throw e;
    const fresh = await api.get<Conversation>(`/api/conversations/${conv.id}`);
    onConversation?.(fresh);
    return run(fresh);
  }
}

export const rememberPlaintext = (id: string, content: string) => plaintext.set(id, content.slice(0, 300));
